import type { CustomObjectField as ApiCustomObjectField } from '@kizen/api/custom-object';
import type { Role } from '@kizen/api/role';
import type { ChangeEvent, FocusEvent } from 'react';
import type { FieldInputValue } from 'utility/getFieldInputValue';
import type { EditingTeamMember, TeamMemberFieldName } from './types';

import { useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import FieldInput from 'components/Fields/FieldInput';
import Dropdown from '__components/Fields/FieldInput/Dropdown';
import { getFieldsDescription } from 'components/Fields/FieldInput/helpers';
import { FieldLayout } from 'components/Fields/Form';
import DateInput from '__components/Inputs/DateInput';
import { getDataQAForInput } from 'components/Inputs/helpers';
import Switch from 'components/Kizen/Switch';
import Cols from '__components/Layout/Cols';
import MultiSelect from '__components/Inputs/MultiSelect';
import SelectAllButton from '__components/Inputs/MultiSelect/SelectAllButton';
import ApplySelectButton from '__components/Inputs/Select/ApplyButton';
import ClearSelectButton from '__components/Inputs/Select/ClearButton';
import {
  Entities,
  useSelectTypeaheadWithScroll,
} from '__components/Inputs/Select/hooks';
import TextInput from '__components/Inputs/TextInput';
import EmailAddressTextInput from '__components/Inputs/TextInput/presets/EmailAddress';
import PhoneNumberTextInput from '__components/Inputs/TextInput/presets/PhoneNumber';
import PriceNumberTextInput, {
  toPriceNumber,
} from 'components/Inputs/TextInput/presets/PriceNumber';
import { DEFAULT_TEMPLATE_ID } from 'components/Modals/ApplyTemplateModal';
import { KeyBoardContext } from 'hooks/keyboardEventHandler/useKeyBoardContext';
import { useKeyListeners } from 'hooks/keyboardEventHandler/useKeyListeners';
import { permissionGroupsQuery } from 'query/permission-group';
import {
  getBusinessTeamMembersObject,
  getChosenBusiness,
} from 'store/authentication/selectors';
import { FIELD_TYPES } from 'utility/constants';
import { snakeToCamelCaseKeys } from '__services/helpers';
import CustomObjectsService from 'services/CustomObjectsService';
import ToolbarService from 'services/ToolbarService';
import { StyledFormRow, StyledFormRowLast, SwitchControl } from './styles';
import { useTheme } from 'hooks/useTheme';
import { UITheme } from '@kizen/kds/Colors';

type CustomObjectField = SnakeToCamelCase<ApiCustomObjectField>;

type Option = {
  label: string;
  value: string;
};

type InputError = {
  errorMessage: string;
  removeOnSubmit?: boolean;
};

type TeamMemberFormProps = {
  disableEmail: boolean;
  fieldErrors: null | Record<string, string>;
  fieldsValidationProps(fieldId: string): void;
  fieldsValues: Record<string, FieldInputValue>;
  isEdit: boolean;
  onChange(field: TeamMemberFieldName, value: any, error?: InputError): void;
  onFieldsChange(field: string, value: any, error?: InputError): void;
  roles: SnakeToCamelCase<Role>[];
  teamMemberFields: CustomObjectField[];
  validationProps(field: TeamMemberFieldName): any;
  values: EditingTeamMember;
};

const valueToSelectOption = (
  values: { id: string; name: string }[]
): Option[] =>
  values.map((value) => ({
    value: value.id,
    label: value.name,
  }));

const selectOptionToValues = (options: Option[]) =>
  options.map((option) => ({
    id: option.value,
    name: option.label,
  }));

const StagedField = ({
  field,
  fetchUrl,
  entityId,
  value,
  initialValue,
  error,
  onChange,
  objectId,
  validationProps,
  getKeyListenersProps,
}: any) => {
  const { withNameDescription } = getFieldsDescription(field);

  const handleChange = useCallback(
    (value: any, _event: any, err: any) => {
      onChange(field.id, value, err);
    },
    [onChange, field.id]
  );

  return (
    <div className={`span${field.meta.cols || 1}`}>
      <FieldInput
        fieldId={field.id}
        required={false}
        margin
        inModal
        field={field}
        fetchUrl={fetchUrl}
        entityId={entityId}
        access={field.access}
        value={value}
        initialValue={initialValue}
        error={error}
        onChange={handleChange}
        serviceToUse={CustomObjectsService}
        objectId={objectId}
        isGetFullObject
        {...getDataQAForInput(`teammember-${field.name}`, field.fieldType)}
        labelInfo={withNameDescription}
        description={withNameDescription}
        {...validationProps}
        minWidth={null}
        {...getKeyListenersProps(field.id)}
      />
    </div>
  );
};

const TeamMemberFormRenderFunc = (props: TeamMemberFormProps) => {
  const {
    isEdit = false,
    values,
    fieldsValues,
    onChange,
    onFieldsChange,
    roles,
    disableEmail,
    validationProps,
    fieldsValidationProps,
    teamMemberFields,
    fieldErrors,
  } = props;
  const teamMemberObject = useSelector(getBusinessTeamMembersObject);
  const phoneRef = useRef(null);
  const dateRef = useRef(null);
  const selectRef = useRef(null);
  const initialValuesRef = useRef(fieldsValues);
  const roleOptions = useMemo(
    () => roles && valueToSelectOption(roles),
    [roles]
  );
  const { t } = useTranslation();
  const chosenBusiness = useSelector(getChosenBusiness);

  const { data: permissionGroups = [] } = useQuery(
    permissionGroupsQuery({
      staleTime: Infinity,
      select: (data) => {
        return snakeToCamelCaseKeys(data);
      },
    })
  );

  const fetch = useCallback(
    async ({ search = '', page = 1 }) => {
      const data = await ToolbarService.listTemplates(page, 20, 'name', search);
      if (page === 1) {
        return {
          ...data,
          results: [
            {
              id: DEFAULT_TEMPLATE_ID,
              employee_access: 'View',
              name: t('Default Template'),
              owner: {
                display_name: t('Kizen Administrator'),
                static: true,
              },
            },
            ...data.results,
          ],
        };
      }
      return data;
    },
    [t]
  );

  const [typeaheadProps, typeaheadState] = useSelectTypeaheadWithScroll({
    entity: Entities.ToolbarTemplates,
    fetch,
    selectRef,
    shouldRefetchOnMount: false,
    shouldResetOptions: false,
    keepPreviousData: true,
  });

  const options = useMemo(() => {
    return Array.isArray(typeaheadProps.options) &&
      typeaheadProps.options.length !== 0
      ? typeaheadProps.options
      : [
          {
            value: DEFAULT_TEMPLATE_ID,
            label: t('Default Template'),
          },
        ];
  }, [typeaheadProps.options, t]);

  const { themeOptions } = useTheme();

  const themeValue = useMemo(() => {
    return (
      themeOptions.find((option) => option.value === values.theme) ??
      (values.theme
        ? {
            value: values.theme,
            label: values.theme,
          }
        : themeOptions[0])
    );
  }, [themeOptions, values.theme]);

  const permissionGroupOptions = useMemo(() => {
    return permissionGroups.map((pg) => ({
      value: pg.id,
      label: pg.name,
    }));
  }, [permissionGroups]);

  const { assignFieldHandle, getKeyListenersProps } = useKeyListeners(
    [
      { id: 'team-member-first-name' },
      { id: 'team-member-last-name' },
      { id: 'team-member-email' },
      { id: 'team-member-phone-number' },
      { id: 'start-date' },
      { id: 'team-roles' },
      { id: 'permission-groups' },
      !isEdit ? { id: 'toolbar-templates' } : null,
      { id: 'theme' },
      { id: 'track-annual-sales-goal' },
      values && values.trackAnnualSalesGoal
        ? { id: 'annual-sales-goal' }
        : null,
      ...teamMemberFields,
    ].filter(Boolean)
  );

  const getFieldValidationProps = useCallback(
    (id: string) => {
      return Object.keys(fieldErrors || {}).length
        ? {}
        : fieldsValidationProps(id);
    },
    [fieldsValidationProps, fieldErrors]
  );

  return (
    <KeyBoardContext.Provider value={{ assignFieldHandle }}>
      <>
        <StyledFormRow>
          <TextInput
            required
            label={t('First Name')}
            placeholder={t('Enter First Name')}
            value={values.firstName}
            onChange={(value: string) => {
              onChange('firstName', value);
            }}
            {...validationProps('firstName')}
            {...getDataQAForInput('team-member-first-name')}
            {...getKeyListenersProps('team-member-first-name')}
          />
          <TextInput
            required
            label={t('Last Name')}
            placeholder={t('Enter Last Name')}
            value={values.lastName}
            onChange={(value: string) => {
              onChange('lastName', value);
            }}
            {...validationProps('lastName')}
            {...getDataQAForInput('team-member-last-name')}
            {...getKeyListenersProps('team-member-last-name')}
          />
        </StyledFormRow>
        <StyledFormRow fullWidth>
          <EmailAddressTextInput
            label={t('Email (Username)')}
            disabled={disableEmail}
            placeholder={t('Email@Domain.com')}
            value={values.email}
            required
            onChange={(
              value: string,
              _ev: ChangeEvent<HTMLInputElement>,
              err?: InputError
            ) => {
              onChange('email', value, err);
            }}
            {...validationProps('email')}
            {...getDataQAForInput('team-member-email', 'email')}
            {...getKeyListenersProps('team-member-email')}
          />
        </StyledFormRow>
        <StyledFormRow fullWidth>
          <PhoneNumberTextInput
            ref={phoneRef}
            label={t('Phone Number')}
            placeholderNumber={t('Enter Phone Number')}
            value={values.phone}
            inModal
            onChange={(
              value: string,
              _ev: ChangeEvent<HTMLInputElement>,
              err?: InputError
            ) => {
              onChange('phone', value, err);
            }}
            {...validationProps('phone')}
            defaultCountry={chosenBusiness.phone_default_region}
            {...getDataQAForInput('team-member-phone-number', 'phonenumber')}
            {...getKeyListenersProps('team-member-phone-number')}
          />
        </StyledFormRow>
        <StyledFormRow fullWidth>
          <DateInput
            ref={dateRef}
            variant="outline"
            labelInfo={t(
              'The user will be invited immediately, but this will be used for the start of quota calculation.'
            )}
            fieldType={FIELD_TYPES.Date.type}
            label={t('Start Date')}
            placeholder={t('Choose Date')}
            value={values.startDate}
            onChange={(value: Date) => {
              onChange('startDate', value);
            }}
            {...validationProps('startDate')}
            classNamePrefix="DateSelector"
            {...getKeyListenersProps('start-date')}
          />
        </StyledFormRow>
        <StyledFormRow fullWidth>
          <MultiSelect
            label={t('Set Role(s)')}
            placeholder={t('Choose Role(s)')}
            options={roleOptions}
            value={valueToSelectOption(values.roles)}
            menuInline
            menuTopButton={<SelectAllButton />}
            menuLeftButton={<ClearSelectButton />}
            menuRightButton={<ApplySelectButton />}
            onChange={(value: Option[]) => {
              onChange('roles', selectOptionToValues(value));
            }}
            {...validationProps('roles')}
            {...getDataQAForInput('team-roles', 'selector')}
            {...getKeyListenersProps('team-roles')}
          />
        </StyledFormRow>
        <StyledFormRow fullWidth>
          <MultiSelect
            label={t('Add Additional Permission Group(s)')}
            placeholder={t('Choose Additional Permission Group(s)')}
            options={permissionGroupOptions}
            value={valueToSelectOption(values.permissionGroups)}
            menuInline
            menuTopButton={<SelectAllButton />}
            menuLeftButton={<ClearSelectButton />}
            menuRightButton={<ApplySelectButton />}
            onChange={(value: Option[]) => {
              onChange('permissionGroups', selectOptionToValues(value));
            }}
            {...validationProps('permissionGroups')}
            {...getKeyListenersProps('permission-groups')}
          />
        </StyledFormRow>
        {isEdit ? null : (
          <StyledFormRow fullWidth>
            <Dropdown
              menuLeftButton={null}
              menuRightButton={null}
              field={{
                fieldType: FIELD_TYPES.Dropdown.type,
                options: options,
              }}
              isGetFullObject
              ref={selectRef}
              label={t('Custom Toolbar Template')}
              value={values.toolbarTemplateId}
              menuInline
              onChange={(option: Option) => {
                onChange('toolbarTemplateId', option.value);
              }}
              {...typeaheadProps}
              options={options}
              isLoading={typeaheadState.loading}
              {...getKeyListenersProps('toolbar-templates')}
              {...getDataQAForInput('toolbar-templates', 'selector')}
              onFocus={(ev: FocusEvent<HTMLSelectElement>) => {
                typeaheadProps.onFocus(ev);
                getKeyListenersProps?.('toolbar-templates')?.onFocus();
              }}
            />
          </StyledFormRow>
        )}
        <StyledFormRow fullWidth>
          <Dropdown
            menuLeftButton={null}
            menuRightButton={null}
            field={{
              fieldType: FIELD_TYPES.Dropdown.type,
              options: themeOptions,
            }}
            label={t('Theme')}
            value={themeValue}
            menuInline
            onChange={(value: UITheme) => {
              onChange('theme', value);
            }}
            options={themeOptions}
            {...validationProps('theme')}
            {...getKeyListenersProps('theme')}
            {...getDataQAForInput('theme', 'selector')}
            onFocus={(ev: FocusEvent<HTMLSelectElement>) => {
              typeaheadProps.onFocus(ev);
              getKeyListenersProps?.('theme')?.onFocus();
            }}
          />
        </StyledFormRow>
        {/* This is actually required, even though not represented in validateTeamMemberForm.
          We default to 0 when submitting. */}
        <StyledFormRowLast fullWidth checked={values.trackAnnualSalesGoal}>
          <Cols columns={2}>
            <SwitchControl
              label={t('Track Annual Quota')}
              {...getDataQAForInput('track-annual-sales-goal')}
              margin={!!teamMemberFields.length}
            >
              <Switch
                checked={values.trackAnnualSalesGoal}
                onChange={() => {
                  onChange(
                    'trackAnnualSalesGoal',
                    !values.trackAnnualSalesGoal
                  );
                }}
                {...validationProps('trackAnnualSalesGoal')}
                {...getKeyListenersProps('track-annual-sales-goal')}
              />
            </SwitchControl>
            {values.trackAnnualSalesGoal ? (
              <PriceNumberTextInput
                label={t('Annual Quota')}
                placeholder={t('0.00')}
                value={
                  values.annualSalesGoal || values.annualSalesGoal === 0
                    ? values.annualSalesGoal
                    : ''
                }
                inModal
                required
                onChange={(value: string) => {
                  onChange('annualSalesGoal', toPriceNumber(value));
                }}
                {...validationProps('annualSalesGoal')}
                fieldType={FIELD_TYPES.Money.type}
                {...getKeyListenersProps('annual-sales-goal')}
              />
            ) : null}
          </Cols>
        </StyledFormRowLast>
        {teamMemberFields.length ? (
          <FieldLayout>
            {teamMemberFields.map((field) => {
              return (
                <StagedField
                  key={field.id}
                  fieldId={field.id}
                  required={false}
                  margin
                  inModal
                  field={field}
                  fetchUrl={`custom-objects/${teamMemberObject.id}`}
                  entityId={values.id}
                  access={field.access}
                  value={fieldsValues[field.id]}
                  initialValue={initialValuesRef.current[field.id]}
                  error={
                    fieldErrors?.[field.id]
                      ? { message: fieldErrors[field.id] }
                      : null
                  }
                  onChange={onFieldsChange}
                  objectId={teamMemberObject.id}
                  validationProps={getFieldValidationProps(field.id)}
                  getKeyListenersProps={getKeyListenersProps}
                />
              );
            })}
          </FieldLayout>
        ) : null}
      </>
    </KeyBoardContext.Provider>
  );
};

export default TeamMemberFormRenderFunc;
